import { differenceInMinutes } from 'date-fns'
import { PolicyWindow } from 'typings/common_defs'

type ShiftTimes = {
  startsAt?: string
  endsAt?: string
}

export function getShiftDurationMinutes(shift: ShiftTimes): number | undefined {
  if (!shift.startsAt || !shift.endsAt) {
    return undefined
  }
  return differenceInMinutes(new Date(shift.endsAt), new Date(shift.startsAt))
}

/** Minutes shown in copy; capped at shift length when shorter than the policy. */
export function getEffectiveChargeMinutes(
  chargeMinutes: number,
  shift?: ShiftTimes
): number {
  const shiftDurationMinutes = shift ? getShiftDurationMinutes(shift) : undefined
  if (shiftDurationMinutes == null) {
    return chargeMinutes
  }
  return Math.min(chargeMinutes, shiftDurationMinutes)
}

export function formatChargeHoursFromMinutes(chargeMinutes: number): string {
  const hours = Math.floor(chargeMinutes / 60)
  const minutes = chargeMinutes % 60
  const parts: string[] = []

  if (hours > 0) {
    parts.push(hours === 1 ? '1 hour' : `${hours} hours`)
  }
  if (minutes > 0) {
    parts.push(minutes === 1 ? '1 minute' : `${minutes} minutes`)
  }
  if (parts.length === 0) {
    return '0 hours'
  }
  return parts.join(' and ')
}

/** Policy list copy, e.g. "50% of the shift cost" or "4 hours of the shift cost". */
export function formatPolicyWindowListCharge(policy: PolicyWindow): string {
  if (policy.chargeMinutes != null) {
    return `${formatChargeHoursFromMinutes(policy.chargeMinutes)} of the shift cost`
  }
  if (policy.chargePercentage != null) {
    return `${policy.chargePercentage}% of the shift cost`
  }
  return 'the applicable shift cost'
}

/** Active-window warning when cancelling an entire shift. */
export function formatPolicyWindowCancelShiftCharge(
  policy: PolicyWindow,
  shift?: ShiftTimes
): string {
  if (policy.chargeMinutes != null) {
    const chargeMinutes = getEffectiveChargeMinutes(policy.chargeMinutes, shift)
    return `for ${formatChargeHoursFromMinutes(chargeMinutes)} of the shift cost if you cancel this shift`
  }
  if (policy.chargePercentage != null) {
    return `${policy.chargePercentage}% if you cancel this shift`
  }
  return 'the applicable cancellation fee if you cancel this shift'
}

/** Active-window warning when removing workers from a shift. */
export function formatPolicyWindowRemoveWorkerCharge(
  policy: PolicyWindow,
  shift?: ShiftTimes
): string {
  if (policy.chargeMinutes != null) {
    const chargeMinutes = getEffectiveChargeMinutes(policy.chargeMinutes, shift)
    return `for ${formatChargeHoursFromMinutes(chargeMinutes)} of each removed worker's cost`
  }
  if (policy.chargePercentage != null) {
    return `${policy.chargePercentage}% of each removed worker's cost`
  }
  return 'the applicable cancellation fee for each removed worker'
}

export function getPolicyWindowKey(policy: PolicyWindow): string {
  return `policy-${policy.cutoffTimeHours}-${policy.chargePercentage ?? 'pct'}-${policy.chargeMinutes ?? 'min'}`
}

export function getPolicyWindowTrackingProperties(
  policy: PolicyWindow | undefined
): {
  policyWindowCutoffTimeHours?: number
  policyWindowChargePercentage?: number
  policyWindowChargeMinutes?: number
} {
  if (!policy) {
    return {}
  }
  return {
    policyWindowCutoffTimeHours: policy.cutoffTimeHours,
    ...(policy.chargePercentage != null
      ? { policyWindowChargePercentage: policy.chargePercentage }
      : {}),
    ...(policy.chargeMinutes != null
      ? { policyWindowChargeMinutes: policy.chargeMinutes }
      : {}),
  }
}
